/**
 * Printers Page
 * 
 * Dedicated page for printer management accessible by OWNER, ADMIN, and CASHIER.
 * Features:
 * - QZ Tray configuration (primary)
 * - Legacy printer management
 * - Print job monitoring
 * - Printer troubleshooting
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useQZPrint, QZStatus } from '../contexts/QZPrintContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Printer,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Play,
  Settings2,
  Download,
  AlertCircle,
  ExternalLink,
  Trash2,
  Edit,
  Plus,
  Clock,
  Usb,
  AlertTriangle,
  Info,
} from 'lucide-react';

export default function Printers() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { currentOutlet } = useOutlet();
  const {
    status: qzStatus,
    isConnected: qzConnected,
    error: qzError,
    printers: qzPrinters,
    printerConfig: qzPrinterConfig,
    loading: qzLoading,
    connect: qzConnect,
    disconnect: qzDisconnect,
    refreshPrinters: qzRefreshPrinters,
    configurePrinter: qzConfigurePrinter,
    testPrint: qzTestPrint,
    canPrintReceipt,
  } = useQZPrint();

  const [activeTab, setActiveTab] = useState('qz-tray');
  const [scanning, setScanning] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  
  // Legacy printers state
  const [legacyPrinters, setLegacyPrinters] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [loadingLegacy, setLoadingLegacy] = useState(false);
  
  // Receipt settings
  const [receiptSettings, setReceiptSettings] = useState({
    auto_print_on_payment: false,
    show_tax_breakdown: true,
    footer_text: 'Terima kasih atas kunjungan Anda!',
  });

  const isOwner = user?.role === 'OWNER';
  const canDelete = ['OWNER'].includes(user?.role);
  const canEdit = ['OWNER', 'ADMIN'].includes(user?.role);

  // Fetch legacy printers and print jobs
  const fetchLegacyData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoadingLegacy(true);
    try {
      const [printersResp, jobsResp, settingsResp] = await Promise.all([
        api.get('/printers/status', { params: { outlet_id: currentOutlet.id } }),
        api.get('/print-jobs', { params: { outlet_id: currentOutlet.id, limit: 20 } }),
        api.get('/settings/receipt', { params: { outlet_id: currentOutlet.id } }),
      ]);
      setLegacyPrinters(printersResp.data);
      setPrintJobs(jobsResp.data);
      setReceiptSettings(settingsResp.data);
    } catch (error) {
      console.error('Failed to fetch printer data:', error);
    } finally {
      setLoadingLegacy(false);
    }
  }, [api, currentOutlet?.id]);

  useEffect(() => {
    fetchLegacyData();
  }, [fetchLegacyData]);

  // Auto-refresh legacy printers every 15 seconds
  useEffect(() => {
    if (!currentOutlet?.id) return;
    const interval = setInterval(fetchLegacyData, 15000);
    return () => clearInterval(interval);
  }, [currentOutlet?.id, fetchLegacyData]);

  // QZ Tray Functions
  const handleScanPrinters = async () => {
    setScanning(true);
    try {
      await qzRefreshPrinters();
      toast.success(`Ditemukan ${qzPrinters.length} printer`);
    } catch (err) {
      toast.error('Gagal scan printer');
    } finally {
      setScanning(false);
    }
  };

  const handleTestPrint = async (role) => {
    const printerName = qzPrinterConfig[role];
    if (!printerName) {
      toast.error(`Pilih printer untuk ${role} terlebih dahulu`);
      return;
    }

    setTesting(prev => ({ ...prev, [role]: true }));
    setTestResults(prev => ({ ...prev, [role]: null }));

    try {
      const result = await qzTestPrint(printerName);
      setTestResults(prev => ({ ...prev, [role]: result }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [role]: { success: false, error: err.message } }));
    } finally {
      setTesting(prev => ({ ...prev, [role]: false }));
    }
  };

  const handlePrinterChange = (role, value) => {
    qzConfigurePrinter(role, value === 'none' ? null : value);
    setTestResults(prev => ({ ...prev, [role]: null }));
  };

  const handleUpdateReceiptSettings = async () => {
    if (!currentOutlet) return;
    try {
      await api.put('/settings/receipt', receiptSettings, {
        params: { outlet_id: currentOutlet.id },
      });
      toast.success('Pengaturan receipt berhasil disimpan');
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    }
  };

  const handleRetryPrintJob = async (jobId) => {
    try {
      await api.post(`/print-jobs/${jobId}/retry`);
      toast.success('Print job diulang');
      fetchLegacyData();
    } catch (error) {
      toast.error('Gagal mengulang print job');
    }
  };

  const getPrintJobStatusIcon = (status) => {
    switch (status) {
      case 'PRINTED':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'SENT':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">Pilih outlet terlebih dahulu</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="printers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="h-6 w-6" />
            Printer Management
          </h1>
          <p className="text-muted-foreground">
            Kelola printer kasir dan dapur untuk outlet {currentOutlet?.name}
          </p>
        </div>
        
        {/* QZ Tray Status Badge */}
        <div className="flex items-center gap-2">
          {qzConnected ? (
            <Badge variant="default" className="bg-green-600">
              <Wifi className="h-3 w-3 mr-1" />
              QZ Tray Connected
            </Badge>
          ) : (
            <Badge variant="destructive">
              <WifiOff className="h-3 w-3 mr-1" />
              QZ Tray Offline
            </Badge>
          )}
        </div>
      </div>

      {/* Warning if Receipt Printer not configured */}
      {!canPrintReceipt && qzConnected && (
        <Alert variant="destructive" data-testid="receipt-printer-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Printer Kasir Belum Dikonfigurasi</AlertTitle>
          <AlertDescription>
            Receipt tidak dapat dicetak. Silakan pilih printer untuk Receipt di bawah.
          </AlertDescription>
        </Alert>
      )}

      {!qzConnected && (
        <Alert variant="destructive" data-testid="qz-offline-warning">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>QZ Tray Tidak Terhubung</AlertTitle>
          <AlertDescription>
            Printer tidak dapat digunakan. Pastikan QZ Tray sudah terinstall dan berjalan di komputer ini.
            <Button variant="link" className="px-1" asChild>
              <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer">
                Download QZ Tray
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="qz-tray" data-testid="tab-qz-tray">
            <Wifi className="h-4 w-4 mr-2" />
            QZ Tray
          </TabsTrigger>
          <TabsTrigger value="receipt-settings" data-testid="tab-receipt-settings">
            <Settings2 className="h-4 w-4 mr-2" />
            Receipt Settings
          </TabsTrigger>
          <TabsTrigger value="print-jobs" data-testid="tab-print-jobs">
            <Clock className="h-4 w-4 mr-2" />
            Print Jobs
          </TabsTrigger>
        </TabsList>

        {/* QZ Tray Tab */}
        <TabsContent value="qz-tray" className="space-y-6">
          {/* Connection Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {qzConnected ? (
                      <Wifi className="h-5 w-5 text-green-500" />
                    ) : (
                      <WifiOff className="h-5 w-5 text-muted-foreground" />
                    )}
                    QZ Tray Connection
                  </CardTitle>
                  <CardDescription>
                    Direct printing ke thermal printer via QZ Tray
                  </CardDescription>
                </div>
                <Badge variant={qzConnected ? 'default' : 'secondary'}>
                  {qzStatus === QZStatus.CONNECTED && 'Connected'}
                  {qzStatus === QZStatus.CONNECTING && 'Connecting...'}
                  {qzStatus === QZStatus.ERROR && 'Error'}
                  {qzStatus === QZStatus.DISCONNECTED && 'Disconnected'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {qzError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Connection Error</AlertTitle>
                  <AlertDescription>{qzError}</AlertDescription>
                </Alert>
              )}

              {!qzConnected && (
                <Alert>
                  <Download className="h-4 w-4" />
                  <AlertTitle>QZ Tray Diperlukan</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>QZ Tray harus diinstall dan berjalan untuk direct printing.</p>
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" asChild>
                        <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-2" />
                          Download QZ Tray
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </Button>
                      <Button onClick={qzConnect} disabled={qzLoading}>
                        {qzLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Wifi className="h-4 w-4 mr-2" />
                        )}
                        Connect
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {qzConnected && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleScanPrinters} disabled={scanning}>
                    {scanning ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Scan Printers
                  </Button>
                  <Button variant="outline" onClick={qzDisconnect}>
                    <WifiOff className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                  {qzPrinters.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {qzPrinters.length} printer ditemukan
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Printer Configuration Cards */}
          {qzConnected && (
            <>
              {/* Receipt Printer - REQUIRED */}
              <PrinterRoleCard
                role="receipt"
                title="Printer Kasir (Receipt)"
                description="Cetak struk pelanggan - WAJIB dikonfigurasi untuk transaksi"
                icon={<Printer className="h-5 w-5 text-green-500" />}
                required
                printers={qzPrinters}
                selectedPrinter={qzPrinterConfig.receipt}
                onPrinterChange={(value) => handlePrinterChange('receipt', value)}
                onTest={() => handleTestPrint('receipt')}
                testing={testing.receipt}
                testResult={testResults.receipt}
                canEdit={true}
              />

              {/* Kitchen Printer */}
              <PrinterRoleCard
                role="kitchen"
                title="Printer Dapur (Kitchen)"
                description="Cetak tiket pesanan makanan ke dapur"
                icon={<Printer className="h-5 w-5 text-orange-500" />}
                printers={qzPrinters}
                selectedPrinter={qzPrinterConfig.kitchen}
                onPrinterChange={(value) => handlePrinterChange('kitchen', value)}
                onTest={() => handleTestPrint('kitchen')}
                testing={testing.kitchen}
                testResult={testResults.kitchen}
                canEdit={true}
              />

              {/* Bar Printer */}
              <PrinterRoleCard
                role="bar"
                title="Printer Bar"
                description="Cetak tiket pesanan minuman ke bar"
                icon={<Printer className="h-5 w-5 text-blue-500" />}
                printers={qzPrinters}
                selectedPrinter={qzPrinterConfig.bar}
                onPrinterChange={(value) => handlePrinterChange('bar', value)}
                onTest={() => handleTestPrint('bar')}
                testing={testing.bar}
                testResult={testResults.bar}
                canEdit={true}
              />
            </>
          )}

          {/* Important Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                Catatan Penting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 text-destructive" />
                <p>
                  <strong className="text-foreground">Receipt</strong> hanya dapat dicetak via QZ Tray. 
                  Browser fallback TIDAK tersedia untuk struk.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500" />
                <p>
                  <strong className="text-foreground">Kitchen/Bar</strong> akan menggunakan 
                  browser print sebagai fallback jika QZ Tray offline.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Settings2 className="h-4 w-4 mt-0.5" />
                <p>
                  Konfigurasi disimpan otomatis. QZ Tray akan auto-reconnect jika terputus.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipt Settings Tab */}
        <TabsContent value="receipt-settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pengaturan Receipt</CardTitle>
              <CardDescription>
                Konfigurasi format dan perilaku cetak struk
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-md border">
                <div>
                  <p className="font-medium">Auto Print Setelah Pembayaran</p>
                  <p className="text-sm text-muted-foreground">
                    Cetak struk otomatis setelah pembayaran berhasil
                  </p>
                </div>
                <Switch
                  checked={receiptSettings.auto_print_on_payment}
                  onCheckedChange={(checked) => 
                    setReceiptSettings({ ...receiptSettings, auto_print_on_payment: checked })
                  }
                  disabled={!canEdit}
                  data-testid="auto-print-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-md border">
                <div>
                  <p className="font-medium">Tampilkan Breakdown Pajak</p>
                  <p className="text-sm text-muted-foreground">
                    Tampilkan detail perhitungan pajak pada struk
                  </p>
                </div>
                <Switch
                  checked={receiptSettings.show_tax_breakdown}
                  onCheckedChange={(checked) => 
                    setReceiptSettings({ ...receiptSettings, show_tax_breakdown: checked })
                  }
                  disabled={!canEdit}
                  data-testid="tax-breakdown-switch"
                />
              </div>

              <div>
                <Label>Footer Text</Label>
                <Input
                  value={receiptSettings.footer_text || ''}
                  onChange={(e) => 
                    setReceiptSettings({ ...receiptSettings, footer_text: e.target.value })
                  }
                  placeholder="Terima kasih atas kunjungan Anda!"
                  disabled={!canEdit}
                  data-testid="footer-text-input"
                />
              </div>

              {canEdit && (
                <Button onClick={handleUpdateReceiptSettings} data-testid="save-receipt-settings">
                  Simpan Pengaturan
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Print Jobs Tab */}
        <TabsContent value="print-jobs" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Print Jobs</CardTitle>
                  <CardDescription>
                    Riwayat dan status print job
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={fetchLegacyData} disabled={loadingLegacy}>
                  {loadingLegacy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {printJobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Belum ada print job</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Printer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {printJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="font-mono">{job.order_number}</TableCell>
                        <TableCell>{job.printer_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{job.job_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getPrintJobStatusIcon(job.status)}
                            <span className={
                              job.status === 'PRINTED' ? 'text-green-500' :
                              job.status === 'FAILED' ? 'text-red-500' :
                              'text-yellow-500'
                            }>
                              {job.status}
                            </span>
                          </div>
                          {job.last_error && (
                            <p className="text-xs text-red-500 mt-1">{job.last_error}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleTimeString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {job.status === 'FAILED' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRetryPrintJob(job.id)}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Printer Role Card Component
function PrinterRoleCard({
  role,
  title,
  description,
  icon,
  required,
  printers,
  selectedPrinter,
  onPrinterChange,
  onTest,
  testing,
  testResult,
  canEdit,
}) {
  return (
    <Card data-testid={`printer-config-${role}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <div>
              <CardTitle className="text-base">
                {title}
                {required && (
                  <Badge variant="destructive" className="ml-2 text-xs">
                    WAJIB
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          {testResult && (
            <Badge variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <>
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Berhasil
                </>
              ) : (
                <>
                  <XCircle className="h-3 w-3 mr-1" />
                  Gagal
                </>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Pilih Printer</Label>
            <Select
              value={selectedPrinter || 'none'}
              onValueChange={onPrinterChange}
              disabled={!canEdit}
            >
              <SelectTrigger data-testid={`select-printer-${role}`}>
                <SelectValue placeholder="Pilih printer..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">-- Tidak digunakan --</span>
                </SelectItem>
                {printers.map((printer) => (
                  <SelectItem key={printer.name} value={printer.name}>
                    {printer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={onTest}
            disabled={!selectedPrinter || testing}
            data-testid={`test-printer-${role}`}
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        </div>
        {testResult && !testResult.success && testResult.error && (
          <p className="text-xs text-destructive mt-2">{testResult.error}</p>
        )}
      </CardContent>
    </Card>
  );
}
